import { EmployeePermissions, NonBlockingEmployeePermissions, Permission } from './Permissions';

// The list of possible permissiongroups is meant to be a superset of all possible roles.
// The allowable permission groups are configured in sitesettings.
// That way, specific permissions will never have to be transported across the wire,
// nor will they have to take up space in tokens / cookies
// Furthermore, permission logic can be changed - collapse many to one or split one into multiple facets,
// without having to remap anything in the data layer, as long as permissiongroupids remain unchanged

export type PermissionGroupId = typeof PermissionGroupIds[number];
export const PermissionGroupIds = [
    '', 'anonymous', 'employee', 'user', 'member', 'siteadmin', 'superadmin'
] as const;

export type PermissionGroup =
    {
        id: PermissionGroupId,
        name: string,
        permissions: Permission[];
    };

const anonymous: PermissionGroup =
{
    id: '',
    name: 'Anonym',
    permissions: [
        'Login',
        'RefreshToken',
        'PortalLogin',
        'Logout',
        'FeatureFlags',
        'CreateUser',
        'RequestPasswordReset',
        'SubmitSurveyAnswer',
        'PublicSurveyReportLink',
        'PublicActionPlanLink',
        'PublicArticle',
        'ViewCustomForm',
        'ViewCustomFormAnswer',
        'SubmitCustomFormAnswer',
        'ViewLearningModules',
        'GetSitePartner',
        'RespondToIncidentMessage'
    ]
};

const employee: PermissionGroup =
{
    id: 'employee',
    name: 'Medarbejder',
    permissions: [
        ...anonymous.permissions,
        ...EmployeePermissions,
        ...NonBlockingEmployeePermissions,
    ]
};

const user: PermissionGroup =
{
    id: 'user',
    name: 'Bruger',
    permissions: [
        ...employee.permissions,
        'ViewSurvey',
        'EditUser',
        'GetActiveUser',
        'ActionPlans',
        'PublicArticle',
        'ViewActivityOverview',
        'EditActionPoints',
        'ViewActionPoints',
        'AssignedSafetyInspections'
    ]
};

const member: PermissionGroup =
{
    id: 'member',
    name: 'AMO Medlem',
    permissions: [
        ...user.permissions,
        'ViewUsers',
        'ViewCourses',
        'ViewActivity',
        'EditActivity',
        'EditSurveys',
        'ViewSurveyReport',
        'EditOrganization',
        'ViewNotifications',
        'EditNotifications',
        'EditLocations',
        'EditPersons',
        'AllSafetyInspections',
        'ViewLocations',
        'ViewCustomFormList',
        'ViewCustomFormAnswerList',
        'ViewArticles',
        'EditArticles',
        'ViewExternalLinks',
        'DocumentArchive',
        'ReferenceLinks',
        'Comments',
        'ManageEmployeePortal'
    ]
};

const siteAdmin: PermissionGroup =
{
    id: 'siteadmin',
    name: 'Administrator',
    permissions: [
        ...member.permissions,
        'UserAdmin', // All

        'DeleteAllSurveyAnswers',
        'SendSurveyRespondentEmails',
        'EditCustomForms',
        'AdminCustomFormAnswers',
        'EditExternalLinks',
        'EditSiteApplicationSettings',
        
        'ManageIncidents',
    ]
};

const superAdmin: PermissionGroup =
{
    id: 'superadmin',
    name: 'System Administrator',
    permissions: [
        ...siteAdmin.permissions,
        'SuperAdmin',
        'EditCourses',
    ]
};

export const PermissionGroups = {
    anonymous,
    employee,
    user,
    member,
    siteAdmin,
    superAdmin,
    all: [
        anonymous,
        employee,
        user,
        member,
        siteAdmin,
        superAdmin
    ]
};
